export interface ScreenWakeLockSentinelLike {
  readonly released?: boolean;
  release(): Promise<void>;
  addEventListener(type: "release", listener: () => void): void;
  removeEventListener(type: "release", listener: () => void): void;
}

export interface ScreenWakeLockProviderLike {
  request(type: "screen"): Promise<ScreenWakeLockSentinelLike>;
}

export type ScreenAwakeState =
  | "unsupported"
  | "inactive"
  | "pending"
  | "active"
  | "paused"
  | "error";

export interface ScreenAwakeSnapshot {
  state: ScreenAwakeState;
  intended: boolean;
  active: boolean;
}

export interface ScreenAwakeContext {
  eligible: boolean;
  visible: boolean;
}

export interface ScreenAwakePresentation {
  status: string;
  detail: string;
  action: string | null;
  pressed: boolean;
  disabled: boolean;
}

type ScreenWakeLockNavigator = {
  wakeLock?: ScreenWakeLockProviderLike;
};

export function browserScreenWakeLockProvider(
  navigatorObject: Navigator,
): ScreenWakeLockProviderLike | null {
  const provider = (
    navigatorObject as unknown as ScreenWakeLockNavigator
  ).wakeLock;
  if (!provider || typeof provider.request !== "function") return null;
  return {
    request: (type) => provider.request(type),
  };
}

export function presentScreenAwake(
  snapshot: ScreenAwakeSnapshot,
): ScreenAwakePresentation {
  switch (snapshot.state) {
    case "unsupported":
      return {
        status: "Screen can sleep",
        detail: "This browser cannot keep the display awake.",
        action: null,
        pressed: false,
        disabled: true,
      };
    case "pending":
      return {
        status: "Asking this browser…",
        detail: "The display is not held awake until the browser confirms.",
        action: "Asking…",
        pressed: false,
        disabled: true,
      };
    case "active":
      return {
        status: "Screen staying awake",
        detail: "This visible tab is asking your device to keep its display on.",
        action: "Let screen sleep",
        pressed: true,
        disabled: false,
      };
    case "paused":
      return {
        status: "Screen awake paused",
        detail: "This tab is hidden. Convoful will ask again when you return.",
        action: "Stop keeping awake",
        pressed: false,
        disabled: false,
      };
    case "error":
      return {
        status: "Screen can sleep",
        detail: "Your browser or device stopped the request. Try again when you’re ready.",
        action: "Try again",
        pressed: false,
        disabled: false,
      };
    case "inactive":
    default:
      return {
        status: "Screen can sleep",
        detail: "Keep this display on while this live session is open.",
        action: "Keep screen awake",
        pressed: false,
        disabled: false,
      };
  }
}

export class ScreenAwakeController {
  private state: ScreenAwakeState;
  private intended = false;
  private eligible = false;
  private visible = true;
  private generation = 0;
  private sentinel: ScreenWakeLockSentinelLike | null = null;
  private sentinelReleaseListener: (() => void) | null = null;

  constructor(
    private readonly provider: ScreenWakeLockProviderLike | null,
    private readonly onChange: (snapshot: ScreenAwakeSnapshot) => void =
      () => undefined,
    private readonly readVisibility?: () => boolean,
  ) {
    this.state = provider ? "inactive" : "unsupported";
  }

  snapshot(): ScreenAwakeSnapshot {
    return {
      state: this.state,
      intended: this.intended,
      active: this.state === "active" && this.sentinel !== null,
    };
  }

  setContext(context: ScreenAwakeContext): void {
    this.eligible = context.eligible;
    this.visible = context.visible;

    if (!this.eligible) {
      void this.stop("inactive", true);
      return;
    }
    if (!this.provider) {
      this.intended = false;
      this.setState("unsupported");
      return;
    }
    if (!this.visible) {
      void this.stop(this.intended ? "paused" : "inactive", false);
      return;
    }
    if (this.intended && this.state !== "active" && this.state !== "pending") {
      void this.request();
      return;
    }
    if (!this.intended && this.state === "paused") {
      this.setState("inactive");
    }
  }

  async enable(): Promise<void> {
    if (!this.eligible) {
      this.intended = false;
      this.setState(this.provider ? "inactive" : "unsupported");
      return;
    }
    if (!this.provider) {
      this.intended = false;
      this.setState("unsupported");
      return;
    }
    this.intended = true;
    if (!this.visible) {
      this.setState("paused");
      return;
    }
    await this.request();
  }

  async disable(): Promise<void> {
    await this.stop("inactive", true);
  }

  private async request(): Promise<void> {
    if (
      !this.provider
      || !this.eligible
      || !this.visible
      || !this.intended
      || this.state === "pending"
      || this.state === "active"
    ) {
      return;
    }
    const generation = ++this.generation;
    this.setState("pending");
    try {
      const sentinel = await this.provider.request("screen");
      if (
        generation !== this.generation
        || !this.eligible
        || !this.visible
        || !this.intended
      ) {
        await sentinel.release().catch(() => undefined);
        return;
      }
      const releaseListener = (): void => {
        this.handleBrowserRelease(sentinel, releaseListener);
      };
      this.sentinel = sentinel;
      this.sentinelReleaseListener = releaseListener;
      sentinel.addEventListener("release", releaseListener);
      if (sentinel.released) {
        this.handleBrowserRelease(sentinel, releaseListener);
        return;
      }
      this.setState("active");
    } catch {
      if (generation !== this.generation) return;
      this.sentinel = null;
      this.sentinelReleaseListener = null;
      this.intended = false;
      this.setState("error");
    }
  }

  private handleBrowserRelease(
    sentinel: ScreenWakeLockSentinelLike,
    releaseListener: () => void,
  ): void {
    if (
      this.sentinel !== sentinel
      || this.sentinelReleaseListener !== releaseListener
    ) {
      return;
    }
    sentinel.removeEventListener("release", releaseListener);
    this.sentinel = null;
    this.sentinelReleaseListener = null;
    this.generation += 1;
    this.visible = this.readVisibility?.() ?? this.visible;
    if (this.intended && !this.visible && this.eligible) {
      this.setState("paused");
      return;
    }
    this.intended = false;
    this.setState(this.provider && this.eligible ? "error" : "inactive");
  }

  private async stop(
    nextState: Exclude<ScreenAwakeState, "active" | "pending" | "error">,
    clearIntent: boolean,
  ): Promise<void> {
    this.generation += 1;
    if (clearIntent) this.intended = false;
    const sentinel = this.sentinel;
    const releaseListener = this.sentinelReleaseListener;
    this.sentinel = null;
    this.sentinelReleaseListener = null;
    if (sentinel && releaseListener) {
      sentinel.removeEventListener("release", releaseListener);
    }
    this.setState(this.provider ? nextState : "unsupported");
    if (sentinel) await sentinel.release().catch(() => undefined);
  }

  private setState(state: ScreenAwakeState): void {
    if (this.state === state) return;
    this.state = state;
    this.onChange(this.snapshot());
  }
}
